import React, { useEffect, useRef, useState } from 'react';

/**
 * IB-758 — Budget Versions v2, Phase D.
 *
 * Reason-gated confirmation for editing a frozen (locked) version. Editing a
 * historical snapshot is a deliberate act, so a non-blank reason is required —
 * it is stored in the version-history audit trail alongside who edited it, when,
 * and exactly what changed. On confirmation this POSTs budgets.edit-snapshot,
 * which captures the "edit" snapshot and redirects the user into edit mode; the
 * endpoint returns a redirect that axios follows, so we navigate the page to the
 * resolved edit-mode URL.
 */
export default function EditSnapshotModal({ budget, editUrl, onClose }) {
    const [reason, setReason] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        textareaRef.current?.focus();

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const canSubmit = reason.trim().length > 0 && !isSaving;

    const submit = () => {
        if (!canSubmit) {
            return;
        }

        setIsSaving(true);
        setError(null);

        axios
            .post(editUrl.replace('budget_id', budget.id), { reason: reason.trim() })
            .then((res) => {
                // The endpoint returns a redirect axios follows transparently; the
                // resolved URL is the edit-mode landing. Navigate the page there.
                const target = res.request && res.request.responseURL;
                if (target) {
                    window.location.href = target;
                } else {
                    window.location.reload();
                }
            })
            .catch((e) => {
                setIsSaving(false);
                const responseError =
                    (e && e.response && e.response.data && e.response.data.errors && e.response.data.errors.reason && e.response.data.errors.reason[0]) ||
                    (e && e.response && e.response.data && e.response.data.message);
                setError(responseError || $t('Something went wrong. Please try again.'));
            });
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-z-100 tw-flex tw-items-center tw-justify-center tw-p-4">
            <div className="tw-fixed tw-inset-0 tw-bg-black/40" onClick={onClose} aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="edit-snapshot-title"
                className="tw-relative tw-z-10 tw-w-full tw-max-w-lg tw-rounded-xl tw-bg-white tw-shadow-primary"
            >
                <div className="tw-p-6">
                    <h2 id="edit-snapshot-title" className="tw-text-lg tw-font-bold tw-text-charcoal">
                        {$t('Edit historical snapshot — {{name}} (v{{version}})', { name: budget.name, version: budget.version })}
                    </h2>
                    <p className="tw-mt-2 tw-text-sm tw-text-charcoal-75">
                        {$t(
                            'This changes a frozen version. A reason is required and will be stored in the version history alongside who edited it, when, and exactly what changed.',
                        )}
                    </p>
                    <label htmlFor="edit-snapshot-reason" className="tw-mt-4 tw-block tw-text-sm tw-font-medium tw-text-charcoal">
                        {$t('Reason for this edit:')}
                    </label>
                    <textarea
                        id="edit-snapshot-reason"
                        ref={textareaRef}
                        rows={3}
                        maxLength={500}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-grey tw-p-3 tw-text-sm tw-text-charcoal focus:tw-border-blue focus:tw-outline-none"
                    />
                    {error && <p className="tw-mt-2 tw-text-sm tw-text-red">{error}</p>}
                </div>
                <div className="tw-flex tw-justify-end tw-gap-x-3 tw-border-t tw-border-grey tw-px-6 tw-py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="tw-rounded-lg tw-border tw-border-charcoal-25 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-charcoal hover:tw-border-charcoal-50"
                    >
                        {$t('Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canSubmit}
                        className="tw-rounded-lg tw-bg-blue tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-blue-75 disabled:tw-opacity-40"
                    >
                        {$t('OK')}
                    </button>
                </div>
            </div>
        </div>
    );
}
